import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../../../../../app/rootReducer';
import { AppDispatch } from '../../../../../app/store';
import { FlexCol, FlexRow } from '../../../../../common/components/v2';
import { FeatureFlagsContext } from '../../../../../common/featureFlagsWrapper';
import { getFormattedTimestamp } from '../../../../../common/helpers';
import {
  closeDrawer,
  drawerCoordActions,
  requestOpen,
} from '../../../drawer/drawerCoordSlice';
import { notesDraftActions } from '../../../notes/notesDraftSlice';
import detailStyles from '../../../styles.module.scss';
import { useETI } from '../hooks';
import styles from '../styles.module.scss';
import { ETIFormData, isETIV2 } from '../types';
import ETIBuilderDrawer, { ETIBuilderDrawerHandle } from './ETIBuilderDrawer';
import ETIGeneratorModal from './ETIGeneratorModal';

export interface ETICreateButtonProps {
  taskId: number;
  onSubmit: (formData: ETIFormData) => Promise<boolean>;
  policyNumber: string;
  isExisting: boolean;
}

const ETIComponent = ({
  taskId,
  onSubmit,
  isExisting,
  policyNumber,
}: ETICreateButtonProps) => {
  const drawerRef = useRef<ETIBuilderDrawerHandle>(null);
  const { eti, isLoading } = useETI(taskId, isExisting);
  const { flags } = useContext(FeatureFlagsContext);
  const useBuilderDrawer = !!flags.uwEtiBuilderDrawerEnabled;

  const dispatch = useDispatch<AppDispatch>();
  const activeDrawer = useSelector(
    (state: RootState) => state.drawerCoord.activeDrawer,
  );
  const notesOpenRequest = useSelector(
    (state: RootState) => state.drawerCoord.notesOpenRequest,
  );
  const isOpen = activeDrawer?.type === 'eti' && activeDrawer.taskId === taskId;

  useEffect(() => {
    if (!notesOpenRequest || notesOpenRequest.taskId !== taskId) return;
    dispatch(drawerCoordActions.notesOpenRequestHandled());
    const openNotes = () => {
      dispatch(drawerCoordActions.drawerOpened({ type: 'notes' }));
      dispatch(notesDraftActions.openDrawer());
    };
    if (drawerRef.current) {
      drawerRef.current.requestCloseAndThen(openNotes);
    } else {
      openNotes();
    }
  }, [notesOpenRequest, taskId, dispatch]);

  const handleOpenETIGenerator = useCallback(() => {
    dispatch(requestOpen({ type: 'eti', taskId }));
    drawerRef.current?.expand();
  }, [dispatch, taskId]);

  const handleETIClick = useCallback(() => {
    if (!eti) {
      handleOpenETIGenerator();
      return;
    }

    if (isETIV2(eti)) {
      if (eti.status === 'PUBLISHED') {
        window.open(
          `/workbench/eti/${taskId}/preview`,
          '_blank',
          'noopener,noreferrer',
        );
        return;
      }

      if (eti.status === 'SUBMITTED') {
        window.open(
          `/workbench/eti/${taskId}/answer`,
          '_blank',
          'noopener,noreferrer',
        );
        return;
      }
    }

    handleOpenETIGenerator();
  }, [eti, handleOpenETIGenerator, taskId]);

  return (
    <>
      <FlexRow className={detailStyles.MarginTop}>
        <FlexCol sm={3} className={detailStyles.TaskLeftCell}>
          Form
        </FlexCol>
        <FlexCol lg={6} className={detailStyles.TaskInfoCell}>
          {eti ? (
            <div
              className={styles.ValueContainer}
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'nowrap',
              }}
            >
              <span
                className={styles.Value}
                onClick={handleETIClick}
                style={{
                  flexBasis: 'auto',
                  maxWidth: 'none',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >{`ETI_${policyNumber} (${eti.status})`}</span>
              <span
                className={styles.Date}
                style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                {getFormattedTimestamp(
                  eti.submittedAt ||
                    eti.publishedAt ||
                    eti.updatedAt ||
                    eti.createdAt,
                )}
              </span>
            </div>
          ) : (
            <div
              className={styles.CreateButton}
              onClick={handleOpenETIGenerator}
            >
              Create ETI
            </div>
          )}
        </FlexCol>
      </FlexRow>
      {useBuilderDrawer ? (
        <ETIBuilderDrawer
          ref={drawerRef}
          eti={eti}
          isLoading={isLoading}
          taskId={taskId}
          onSubmit={onSubmit}
          isOpen={isOpen}
          onClose={() => dispatch(closeDrawer({ type: 'eti', taskId }))}
        />
      ) : (
        <ETIGeneratorModal
          eti={eti}
          isLoading={isLoading}
          taskId={taskId}
          onSubmit={onSubmit}
          isOpen={isOpen}
          onClose={() => dispatch(closeDrawer({ type: 'eti', taskId }))}
        />
      )}
    </>
  );
};

export default ETIComponent;
